import { logger } from "../../../logger"
/**
 * Shadow Performance Monitor
 *
 * This utility helps monitor the performance improvements from texture caching
 * in the shadow rendering system. Use this for debugging and optimization.
 */

export class ShadowPerformanceMonitor {
    private frameCount: number = 0
    private textureRegenerationCount: number = 0
    private cacheHitCount: number = 0
    private lastStatsTime: number = Date.now()
    private lastCacheSize: number = 0
    private lastHash: string = ""
    private shadowRendering: any // ShadowRendering instance

    constructor(shadowRendering: any) {
        this.shadowRendering = shadowRendering
    }

    /**
     * Call this every frame to track performance
     */
    update = () => {
        this.frameCount++

        const stats = this.shadowRendering.getCacheStats()

        // Track cache hits vs misses based on hash changes
        // If hash changed, it means we need to regenerate texture (cache miss)
        // If hash is the same, it means we can reuse cached texture (cache hit)
        if (stats.lastVisibilityHash !== this.lastHash) {
            this.textureRegenerationCount++
            this.lastHash = stats.lastVisibilityHash
        } else if (stats.lastVisibilityHash) {
            this.cacheHitCount++
        }

        // Log stats every 5 seconds
        const now = Date.now()
        if (now - this.lastStatsTime > 5000) {
            this.logStats()
            this.lastStatsTime = now
        }
    }

    private logStats = () => {
        const stats = this.shadowRendering.getCacheStats()
        const totalFrames = this.frameCount
        const cacheHitRate = totalFrames > 0 ? ((this.cacheHitCount / totalFrames) * 100).toFixed(1) : "0"
        const textureRegenRate =
            totalFrames > 0 ? ((this.textureRegenerationCount / totalFrames) * 100).toFixed(1) : "0"

        logger.info({ tags: ["shadow", "debug"] }, 
            `[Shadow Performance] Frames: ${totalFrames}, Cache Hit Rate: ${cacheHitRate}%, Texture Regen: ${textureRegenRate}%, Cache Size: ${stats.cacheSize}/${stats.maxCacheSize}`,
        )

        // Reset counters
        this.frameCount = 0
        this.textureRegenerationCount = 0
        this.cacheHitCount = 0
    }

    /**
     * Get current performance metrics
     */
    getMetrics = () => {
        const stats = this.shadowRendering.getCacheStats()
        return {
            totalFrames: this.frameCount,
            cacheHitRate: this.frameCount > 0 ? (this.cacheHitCount / this.frameCount) * 100 : 0,
            textureRegenRate: this.frameCount > 0 ? (this.textureRegenerationCount / this.frameCount) * 100 : 0,
            cacheSize: stats.cacheSize,
            maxCacheSize: stats.maxCacheSize,
            needsUpdate: stats.needsUpdate,
        }
    }

    /**
     * Reset all counters
     */
    reset = () => {
        this.frameCount = 0
        this.textureRegenerationCount = 0
        this.cacheHitCount = 0
        this.lastStatsTime = Date.now()
        this.lastHash = ""
    }
}

/**
 * Usage example:
 *
 * // In your main render loop or client update:
 * const shadowMonitor = new ShadowPerformanceMonitor(shadowRendering)
 *
 * // In your render function:
 * shadowMonitor.update()
 *
 * // To get current metrics:
 * const metrics = shadowMonitor.getMetrics()
 * logger.info({ tags: ["shadow", "debug"] }, `Cache hit rate: ${metrics.cacheHitRate.toFixed(1)}%`)
 */
